/* ═══════════════════════════════════════════════════════════════════════════
   PRODUCTS, AND SUBSCRIBING TO THEM

   "Abonner på shampoo og andre artikler basert på bransje" — and the thing
   that makes it work is the sentence before it: *based on the trade*, and
   *the ones that were actually used on you*.

   A salon selling shampoo is obvious. The reason to build this properly is
   that it is not only salons, and it is not a shop. The honest version is:

     1. the person who did the work says what they used — the prototype
        already promises this in as many words: "Vi oppgir alltid hvilke
        produkter som brukes"
     2. that list arrives as a message after the appointment, with the name of
        the person who did it on it
     3. anything on that list can be subscribed to at an interval that matches
        how fast it runs out

   That is a different product from a webshop. The customer is not browsing;
   they are being told what was used on their own hair and offered the same
   thing again before it runs out. Which is also why the interval is per
   product and suggested from the product — a 300 ml shampoo used twice a week
   is an eight-week thing, and massage oil is not.

   ── What a trade sells, and what it does not ───────────────────────────────

   Not every trade sells anything, and the ones that do not get nothing rather
   than a thin invented range. A law firm does not sell you a product after a
   meeting, an auditor does not, an engineer does not. `retail(code)` answers
   null for those and every surface is written to render nothing.

   Three rules held here:

   **No alcohol.** Alkoholloven does not allow it to be advertised, and a
   subscription is advertising with a direct debit attached. The restaurant
   trade sells coffee, oil, and what comes out of its own kitchen. It does
   not sell wine, and there is no bottle in the photographs either.

   **Nothing medical.** The health trade sells nothing here. A subscription to
   something used in a consultation is a prescription by another name, and
   that is not a thing a booking platform may build.

   **Prices are the tenant's.** The figures below are demonstration prices on
   demonstration products. They are marked as such on the record — `demo:
   true` — so that nothing downstream can present them as a real business's
   own price list. The real ones arrive with the tenant's catalogue.
   ═══════════════════════════════════════════════════════════════════════ */

/* ── The ranges ────────────────────────────────────────────────────────────
   Keyed on the industry code — this is data, so the code may be a string
   here; it is the surfaces that may never name one.

   `every` is the suggested interval in weeks, and it is a property of the
   product rather than a setting: it is how long the thing lasts, which is the
   one number the customer cannot be expected to work out for themselves. */
const RANGES = {
  hair: {
    noun: 'Aftercare',
    lead: 'What was used on your hair, and what keeps it that way at home.',
    items: [
      { id: 'hair-shampoo',   name: 'Shampoo, 300 ml',            kind: 'Wash',      price: 329, every: 8,  note: 'For coloured hair. Sulphate-free.' },
      { id: 'hair-condition', name: 'Conditioner, 300 ml',        kind: 'Wash',      price: 329, every: 10, note: 'Used after every wash.' },
      { id: 'hair-mask',      name: 'Treatment mask, 200 ml',     kind: 'Treatment', price: 449, every: 12, note: 'Once a week, ten minutes.' },
      { id: 'hair-oil',       name: 'Hair oil, 100 ml',           kind: 'Finish',    price: 389, every: 14, note: 'Ends only, on damp hair.' },
      { id: 'hair-spray',     name: 'Heat protection, 150 ml',    kind: 'Finish',    price: 279, every: 10, note: 'Before the hairdryer, every time.' },
      { id: 'hair-silver',    name: 'Silver shampoo, 250 ml',     kind: 'Colour',    price: 349, every: 12, note: 'Every third wash — keeps the tone.' },
    ],
  },
  beauty: {
    noun: 'Aftercare',
    lead: 'The products used on your skin, and how often they are worth replacing.',
    items: [
      { id: 'skin-cleanser', name: 'Cleanser, 200 ml',        kind: 'Cleanse', price: 429, every: 10, note: 'Morning and evening.' },
      { id: 'skin-serum',    name: 'Serum, 30 ml',            kind: 'Treat',   price: 749, every: 8,  note: 'Under the moisturiser.' },
      { id: 'skin-cream',    name: 'Moisturiser, 50 ml',      kind: 'Treat',   price: 589, every: 10, note: 'Morning and evening.' },
      { id: 'skin-spf',      name: 'SPF 50, 50 ml',           kind: 'Protect', price: 359, every: 8,  note: 'Every morning, all year.' },
      { id: 'nail-oil',      name: 'Cuticle oil, 15 ml',      kind: 'Nails',   price: 189, every: 6,  note: 'Daily, while the gel lasts.' },
    ],
  },
  massage: {
    noun: 'At home',
    lead: 'What was used in the treatment, for keeping it going between visits.',
    items: [
      { id: 'mass-oil',   name: 'Massage oil, 250 ml',      kind: 'Oil',      price: 289, every: 12, note: 'The same oil used in the room.' },
      { id: 'mass-balm',  name: 'Muscle balm, 100 ml',      kind: 'Relief',   price: 249, every: 8,  note: 'After training, or a long day.' },
      { id: 'mass-ball',  name: 'Trigger point ball',       kind: 'Tool',     price: 199, every: 0,  note: 'Bought once. No subscription.' },
      { id: 'mass-salt',  name: 'Bath salt, 1 kg',          kind: 'Recovery', price: 219, every: 10, note: 'Twenty minutes, warm water.' },
    ],
  },
  training: {
    noun: 'Kit and supplements',
    lead: 'What your instructor uses and recommends.',
    items: [
      { id: 'tr-protein', name: 'Protein, 1 kg',        kind: 'Nutrition', price: 549, every: 6,  note: 'One scoop after a session.' },
      { id: 'tr-band',    name: 'Resistance band set',  kind: 'Kit',       price: 349, every: 0,  note: 'Bought once. No subscription.' },
      { id: 'tr-bottle',  name: 'Bottle, 750 ml',       kind: 'Kit',       price: 249, every: 0,  note: 'Bought once. No subscription.' },
      { id: 'tr-electro', name: 'Electrolytes, 30 pcs', kind: 'Nutrition', price: 299, every: 8,  note: 'On the long sessions.' },
    ],
  },
  restaurant: {
    noun: 'From the kitchen',
    /* No alcohol. See the header — alkoholloven, and a subscription is
       advertising with a direct debit attached. */
    lead: 'What we cook with, and what you can take home. No alcohol is sold here.',
    items: [
      { id: 'rest-coffee', name: 'Coffee beans, 500 g',   kind: 'Pantry', price: 189, every: 4, note: 'The same roast we serve.' },
      { id: 'rest-oil',    name: 'Olive oil, 500 ml',     kind: 'Pantry', price: 229, every: 8, note: 'What the kitchen finishes with.' },
      { id: 'rest-sauce',  name: 'House sauce, 250 ml',   kind: 'Pantry', price: 149, every: 6, note: 'Made here, in the kitchen.' },
      { id: 'rest-bread',  name: 'Sourdough, weekly',     kind: 'Bakery', price: 79,  every: 1, note: 'Collected Saturday morning.' },
    ],
  },
  cleaning: {
    noun: 'Supplies',
    lead: 'The products we clean with, if you want them between visits.',
    items: [
      { id: 'cl-all',    name: 'All-purpose, 750 ml',  kind: 'Surfaces', price: 129, every: 8,  note: 'What we use on every surface.' },
      { id: 'cl-floor',  name: 'Floor soap, 1 l',      kind: 'Floors',   price: 149, every: 10, note: 'For oiled and lacquered wood.' },
      { id: 'cl-cloth',  name: 'Microfibre, 6 pcs',    kind: 'Cloths',   price: 189, every: 12, note: 'Washable at 60°.' },
      { id: 'cl-bath',   name: 'Bathroom, 750 ml',     kind: 'Surfaces', price: 139, every: 8,  note: 'Limescale, without the smell.' },
    ],
  },
  home: {
    noun: 'Materials',
    lead: 'What was fitted, and what you will want again for the upkeep.',
    items: [
      { id: 'home-filter', name: 'Ventilation filter',   kind: 'Upkeep', price: 349, every: 26, note: 'Changed twice a year.' },
      { id: 'home-oil',    name: 'Wood oil, 1 l',        kind: 'Upkeep', price: 429, every: 52, note: 'Decking, once a season.' },
      { id: 'home-seal',   name: 'Sealant, 300 ml',      kind: 'Repair', price: 129, every: 0,  note: 'Bought as needed.' },
    ],
  },
  restoration: {
    noun: 'Upkeep',
    lead: 'What keeps the repair holding.',
    items: [
      { id: 'rest-dehum', name: 'Dehumidifier filter', kind: 'Upkeep', price: 299, every: 26, note: 'Twice a year while it runs.' },
      { id: 'rest-meter', name: 'Damp meter',          kind: 'Tool',   price: 549, every: 0,  note: 'Bought once. No subscription.' },
    ],
  },
  childcare: {
    noun: 'Materials',
    lead: 'The books and materials used in the sessions.',
    items: [
      { id: 'ch-work',  name: 'Workbook, per term', kind: 'Books', price: 249, every: 16, note: 'One per term, at their level.' },
      { id: 'ch-set',   name: 'Stationery set',     kind: 'Kit',   price: 179, every: 0,  note: 'Bought once. No subscription.' },
    ],
  },
  /* health, legal, accounting, architect, engineer: nothing. A subscription
     to something used in a consultation is a prescription by another name,
     and a law firm does not sell you a product after a meeting. */
};

/** What a trade sells, or null where it sells nothing. */
export function retail(code) {
  return RANGES[code] || null;
}

/** One trade's products, or an empty list. */
export function productsFor(code) {
  return RANGES[code]?.items || [];
}

/** One product by id, from any trade. */
export function product(id) {
  for (const code in RANGES) {
    const found = RANGES[code].items.find(p => p.id === id);
    if (found) return { ...found, industry: code, demo: true };
  }
  return null;
}

/** The trades that sell anything at all, so no surface has to hold a list. */
export const SELLING = Object.keys(RANGES);

/* ── The intervals ────────────────────────────────────────────────────────
   Weeks, in the words people use. `every: 0` on a product means it is a
   one-off — a resistance band is not a subscription — and the UI drops the
   subscribe control entirely rather than offering one that makes no sense. */
export const INTERVALS = [
  { weeks: 1,  label: 'Every week' },
  { weeks: 2,  label: 'Every other week' },
  { weeks: 4,  label: 'Every month' },
  { weeks: 6,  label: 'Every 6 weeks' },
  { weeks: 8,  label: 'Every 8 weeks' },
  { weeks: 10, label: 'Every 10 weeks' },
  { weeks: 12, label: 'Every 3 months' },
  { weeks: 16, label: 'Every 4 months' },
  { weeks: 26, label: 'Twice a year' },
  { weeks: 52, label: 'Once a year' },
];

/** The nearest offered interval to a product's own suggestion. */
export function intervalFor(weeks) {
  if (!weeks) return null;
  return INTERVALS.reduce((best, i) =>
    Math.abs(i.weeks - weeks) < Math.abs(best.weeks - weeks) ? i : best, INTERVALS[0]);
}

/** "Every 8 weeks" for a number of weeks that may not be on the list. */
export function intervalWords(weeks) {
  const exact = INTERVALS.find(i => i.weeks === weeks);
  return exact ? exact.label : `Every ${weeks} weeks`;
}

/** What a subscription costs a year, which is the figure nobody works out for
    themselves and the one that decides whether they want it. */
export function yearly(price, weeks) {
  if (!weeks) return null;
  return Math.round((52 / weeks) * price);
}
